import { createMap } from '@app/utils/createMap'
import { createSelector } from '@app/utils/createSelector'
import { isTruthy } from '@app/utils/isTruthy'
import { mapObject } from '@app/utils/mapOpject'
import { mergeV2Entity } from '@app/utils/mergeV2Entity'
import { modifyV2EntityAttributes } from '@app/utils/modifyV2EntityAttributes'
import { normalizeSafe } from '@app/utils/normalizer'

import type { ApiAnnouncementResponse } from '@app/packages/api/v2/types/entities'

import { createReduxSlice } from '@app/store/redux_slice'
import type { FirebaseBatch } from '@app/store/types/firebase'

const announcementResponsesSlice = createReduxSlice<{ [key: string]: ApiAnnouncementResponse }>('announcement_responses_models')

export const setAnnouncementResponseStatusSlice = (id: string, status: ApiAnnouncementResponse['attributes']['status']) =>
  announcementResponsesSlice.update(state => {
    const response = state?.[id]
    if (!response) return state
    return { ...state, [id]: modifyV2EntityAttributes(response, { status }) }
  })

export const mergeAnnouncementResponseSlice = (response: ApiAnnouncementResponse) =>
  announcementResponsesSlice.update(state => {
    const existing = state?.[response.id]
    if (!existing) return state
    return { ...state, [response.id]: mergeV2Entity(existing, response) }
  })

/** Firebase mirrors the response status; only the status is taken from it, the rest stays as fetched. */

/** Any response can carry announcement responses along; each one merges over what we hold. */
export const updateAnnouncementResponsesSlice = (payload: unknown) =>
  announcementResponsesSlice.update(state => {
    const { announcement_responses } = normalizeSafe<ApiAnnouncementResponse>(payload)
    if (!announcement_responses) return state
    return { ...state, ...mapObject(announcement_responses, r => mergeV2Entity(state?.[r.id], r)) }
  })

export const applyFirebaseResponseStatusesSlice = (batch: FirebaseBatch) =>
  announcementResponsesSlice.update(state => {
    const responses = createMap(
      Object.values(batch.announcement_responses.models)
        .map(response => {
          const storeResp = state?.[response.id]
          if (!storeResp) return null
          return { ...storeResp, attributes: { ...storeResp.attributes, status: response.attributes.status } }
        })
        .filter(isTruthy),
      r => r.id
    )

    return { ...state, ...responses }
  })

export const announcementResponsesModelsSelector = createSelector([announcementResponsesSlice.selector], models => models ?? {})
